import React, { ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { WMButton, WMButtonVariant, WMMenu, WMTooltip } from '@walkme/wm-ui';
import clsx from 'clsx';
import { AppTableContext } from '../AppTableContext';
import classes from './RowActions.module.scss';
import Restricted from '../../../infrastructure/Permissions/Restricted';
import { AppTableRowAnimateType } from '../model';
import { CC_PERMISSIONS, PERM_TYPES } from '@communication-center/data-objects';
import { withPermissionTooltip } from '../../../infrastructure/Permissions/PermissionsProvider';
import { useTranslation } from 'react-i18next';

interface IRowActions {
    rowData: $TSFixMe;
    children?: ReactElement;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ItemWithTooltip = React.forwardRef((props, ref) => {
    const { children, ...restProps } = props as any;
    const { t } = useTranslation('', { keyPrefix: '' });

    return (
        <WMTooltip title={t('common.dontHavePermissions')}>
            <li {...restProps} className={clsx(restProps.className)}>
                {children}
            </li>
        </WMTooltip>
    );
});

const RowActions = ({ rowData, children }: IRowActions) => {
    const appTableContext = useContext(AppTableContext);
    const { tableDataTestid, actions, addProcessingRowIds, removeProcessingRowIds } = appTableContext;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const isProcessing = useRef(false);

    const showActions = useMemo(() => isMenuOpen, [isMenuOpen]);

    const onClickAction = useCallback(
       (action: any) => async () => {
            if (action.handler && !isProcessing.current) {
                isProcessing.current = true;
                await addProcessingRowIds?.([rowData.id], AppTableRowAnimateType.active);
                try {
                    await action.handler(rowData, appTableContext);
                } finally {
                    await removeProcessingRowIds?.([rowData.id], AppTableRowAnimateType.active);
                    isProcessing.current = false;
                }
            }
        },
        [addProcessingRowIds, rowData, appTableContext, removeProcessingRowIds]
    );

    const onClickContainer = (e: $TSFixMe) => {
        e.stopPropagation();
    };

    const getMenuItemRestrictedToVal = (actions: $TSFixMe) => {
        return actions
            .filter((action: $TSFixMe) => action.isMenuItem && action?.restrictedTo)
            .reduce((unique: $TSFixMe, item: $TSFixMe) => (unique.includes(...item?.restrictedTo) ? unique : [...unique, ...item?.restrictedTo]), []);
    };

    const getAllRestrictedToPermissionsValues = (actions: $TSFixMe) => {
        const acc = actions
            .filter((action: $TSFixMe) => action.isMenuItem && action?.restrictedToPermissions)
            .reduce(
                (unique: $TSFixMe, action: $TSFixMe) => {
                    const key = `${action?.restrictedToPermissions?.[0]}-${action?.restrictedToPermissions?.[1]}`;
                    const hash = unique.hash;
                    if (!hash[key] && action?.restrictedToPermissions) {
                        unique.hash[key] = true;
                        unique.arr = [...unique.arr, action.restrictedToPermissions];
                    }
                    return unique;
                },
                { hash: {}, arr: ([] as unknown) as [CC_PERMISSIONS, PERM_TYPES][] }
            );
        return acc?.arr;
    };

    return (
        <div className={clsx(classes.root, { [classes.showActions]: showActions })}>
            {children && <div className={classes.data}>{children}</div>}

            <div className={classes.actions}>
                <div className={classes.container} onClick={onClickContainer}>
                    {actions
                        ?.filter(action => !action.isMenuItem && !action?.isHidden?.(rowData))
                        .map((action, index) => {
                            const isActive = action?.isActive?.(rowData);
                            const disabled = action.isDisabled?.(rowData);
                            const disabledByPermission = action.isDisabledByPermission;

                            return (
                                <Restricted
                                    to={action?.restrictedTo}
                                    permissions={action?.restrictedToPermissions}
                                    creatorsIds={[rowData?.user?.id]}
                                    key={index}
                                >
                                    {withPermissionTooltip(
                                        <WMButton
                                            key={index}
                                            className={clsx(classes.actionButton, {
                                                [classes.actionButtonActive]: isActive && !disabledByPermission
                                            })}
                                            variant={WMButtonVariant.Text}
                                            iconComponent={action.icon && <action.icon className={classes.actionButtonNoShrink} />}
                                            onClick={onClickAction(action)}
                                            // @ts-expect-error ts-migrate(2322) FIXME: Type 'ReactNode' is not assignable to type 'boolea... Remove this comment to see the full error message
                                            tooltipProps={!disabledByPermission ? { title: action.icon ? action.label : '' } : undefined}
                                            disabled={disabled || disabledByPermission}
                                            data-testid={`${rowData.id}-${action.name || action.label}-${tableDataTestid}`}
                                            classes={{
                                                label: classes.actionButtonLabel
                                            }}
                                        >
                                            {!action.icon && action.label}
                                        </WMButton>,
                                        !action.isDisabledByPermission
                                    )}
                                </Restricted>
                            );
                        })}
                    {actions?.some(action => action.isMenuItem) && (
                        <>
                            <div className={classes.actionButton}>
                                <Restricted
                                    to={getMenuItemRestrictedToVal(actions)}
                                    creatorsIds={[rowData?.user?.id]}
                                    permissions={getAllRestrictedToPermissionsValues(actions)}
                                >
                                    <WMMenu
                                        classes={{
                                            paper: classes.menuPaper,
                                            list: classes.menuList,
                                            menuButton: clsx(classes.menuButton, { [classes.active]: isMenuOpen })
                                        }}
                                        testKey={`${rowData.id}-engagement-actions-menu-${tableDataTestid}`}
                                        autoFocus={false}
                                        disableAutoFocusItem={true}
                                        TransitionProps={{
                                            onEntering: () => {
                                                setIsMenuOpen(true);
                                            }
                                        }}
                                        onClose={() => {
                                            setIsMenuOpen(false);
                                        }}
                                        menuItems={actions
                                            .filter(action => action.isMenuItem && !action?.isHidden?.(rowData))
                                            .map(action => {
                                                const isDisabled = action.isDisabled?.(rowData);
                                                return {
                                                    'data-testid': `${rowData.id}-${action.label}-${tableDataTestid}`,
                                                    label: <div className={classes.menuItemLabel}>{action.label}</div>,
                                                    startIcon: action.icon ? <action.icon className={classes.menuItemIcon} /> : null,
                                                    onClick: isDisabled ? () => {} : onClickAction(action),
                                                    className: clsx(classes.menuItem, action.className, { [classes.menuItemDisabled]: isDisabled }),
                                                    disabled: isDisabled,
                                                    component: isDisabled ? ItemWithTooltip : undefined
                                                };
                                            })}
                                    />
                                </Restricted>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RowActions;
